import { ReportDashboard } from '../views/overview/ReportDashboard'
import { DownloadCsvButton, DownloadXlsxButton } from '../DownloadXlsxButton'
import { SurveyFiguresProvider } from '@/features/studies/report/figures/SurveyFiguresProvider'
import type { SegmentationMapping } from '@/types/components/audiences/segmentation-mapping'
import type { CanonicalAnalysisOutput } from '@digital-twins/shared-types'
import { buildAudienceDescriptors } from '../../buildAudienceDescriptors'
import type { PopulationFilter } from '@/types/routes/test-results'

// ─── Metrics row ──────────────────────────────────────────────────────────────

const RING_RADIUS = 27
const RING_STROKE = 5
const RING_SIZE = (RING_RADIUS + RING_STROKE) * 2  // 64px
const RING_CIRC = 2 * Math.PI * RING_RADIUS

interface MetricCard {
  title: string
  primaryValue?: number
  primaryLabel?: string
}

function MetricsRow({ cards }: { cards: Array<MetricCard> }) {
  if (!cards || cards.length === 0) return null

  return (
    <div className="flex items-center gap-10">
      {cards.map((card, idx) => {
        const pct = Math.min(Math.max(card.primaryValue ?? 0, 0), 100)
        const filled = (pct / 100) * RING_CIRC
        const gap = RING_CIRC - filled
        const gradId = `survey-ring-grad-${idx}`
        return (
          <div key={idx} className="flex items-center gap-4">
            <svg
              width={RING_SIZE}
              height={RING_SIZE}
              viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
              aria-hidden="true"
              className="flex-shrink-0"
            >
              <defs>
                <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="hsl(var(--chart-primary))" />
                  <stop offset="100%" stopColor="var(--chart-secondary, #059669)" />
                </linearGradient>
              </defs>
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                className="stroke-muted-foreground/20"
                strokeWidth={RING_STROKE}
              />
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                stroke={`url(#${gradId})`}
                strokeWidth={RING_STROKE}
                strokeLinecap="round"
                strokeDasharray={`${filled} ${gap}`}
                strokeDashoffset={RING_CIRC / 4}
              />
              <text
                x={RING_SIZE / 2}
                y={RING_SIZE / 2 + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="13"
                fontWeight="700"
                fill="currentColor"
                className="text-foreground"
              >
                {pct}%
              </text>
            </svg>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground leading-none">{card.title}</span>
              {card.primaryLabel && (
                <span className="text-base font-semibold text-foreground leading-snug">{card.primaryLabel}</span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface SurveyDataTabProps {
  studyId?: string
  /** Set on the public share view, where the export goes through `/public`. */
  shareToken?: string
  survey: any
  responses: any[]
  populationAttributes?: string[]
  segmentationMappings?: SegmentationMapping[] | null
  constantContext?: Record<string, string> | null
  executiveSummary?: any
  canonicalAnalysis?: CanonicalAnalysisOutput | null
  studyName?: string
  createdAt?: Date | string
  populationFilter?: PopulationFilter | string
  /** The current analysis has its scale tab book: the download offers "Tables". */
  hasScaleTables?: boolean
  studyWorkbookStatus?: 'legacy' | 'building' | 'ready' | 'failed'
}

export function SurveyDataTab({
  studyId,
  shareToken,
  survey,
  responses,
  populationAttributes = [],
  segmentationMappings = null,
  constantContext = null,
  executiveSummary,
  canonicalAnalysis,
  studyName,
  createdAt,
  populationFilter,
  hasScaleTables = false,
  studyWorkbookStatus = 'legacy',
}: SurveyDataTabProps) {
  const audienceDescriptors = buildAudienceDescriptors({ populationFilter, constantContext })

  const metricCards: Array<MetricCard> = executiveSummary?.metricCards ?? []

  return (
    <div className="flex-1 overflow-y-auto scrollbar-overlay">
      <div className="py-6">
        <div className="rounded-2xl bg-[hsl(var(--report-bg))] p-6">
          {(metricCards.length > 0 || studyId || shareToken) && (
            <div className="flex items-center justify-between -mt-1 mb-6">
              <div className="flex-1">
                {metricCards.length > 0 && <MetricsRow cards={metricCards} />}
              </div>
              {(studyId || shareToken) && (
                <div className="flex items-center gap-2 shrink-0">
                  <DownloadCsvButton
                    studyId={studyId}
                    shareToken={shareToken}
                    studyName={studyName}
                    data-testid="survey-data-tab-download-csv"
                    className="shrink-0 gap-1.5 bg-white dark:bg-background border-border text-foreground hover:bg-muted"
                  />
                  <DownloadXlsxButton
                    studyId={studyId}
                    shareToken={shareToken}
                    studyName={studyName}
                    hasScaleTables={hasScaleTables}
                    studyWorkbookStatus={studyWorkbookStatus}
                    data-testid="survey-data-tab-download-xlsx"
                    className="shrink-0 gap-1.5 bg-white dark:bg-background border-border text-foreground hover:bg-muted"
                  />
                </div>
              )}
            </div>
          )}
          {survey && responses ? (
            /* Plots plan D3: the tab's chart cards draw the figure service's
               figures (owner by study id, share link by token). */
            <SurveyFiguresProvider studyId={studyId} shareToken={shareToken}>
              <ReportDashboard
                survey={survey}
                responses={responses}
                populationAttributes={populationAttributes}
                segmentationMappings={segmentationMappings}
                constantContext={constantContext}
                executiveSummary={executiveSummary}
                canonicalAnalysis={canonicalAnalysis}
                studyName={studyName}
                createdAt={createdAt}
                audienceDescription={audienceDescriptors.join(' · ')}
                hideHeader
              />
            </SurveyFiguresProvider>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              Survey definition not available. Unable to render visualizations.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
